/* ============================================================
   OPORA ANONYMOUS USE GATE  — v3 (Per-Tool Counters)
   Extracted from opora-tools.js so D4 Edge pages can load ~36KB gate
   without the ~96KB legacy hub register/wire stack.
   Exposes window.OporaGate (used by opora-d4-edge-bridge.js).
   ============================================================

   TIER MODEL:
     Anonymous : 2 free COMPLETED runs PER TOOL (ot_runs_{toolId} in localStorage)
     Free trial: 30 days full access (server-enforced, Clerk JWT)
     Solo      : 200/mo ($79)
     Team      : 1,000/mo + 3 seats ($299)
     Pro       : Unlimited + API + 10 seats ($799)

   COUNTED TOOLS (8):
     bid-drafter, rfp-decoder, margin-sentinel, compliance-agent,
     hire-right, handbook-drafter, customer-recovery, lost-bid-autopsy

   FREE / UNCOUNTED:
     ask-opora (basic chat) — rate-limited per IP, never counted
     Legacy ask-opora-deep / ask_deep aliases map to ask-opora (uncounted)
     All calculators, articles, state guides — always free

   ANONYMOUS FLOW (per tool):
     Runs 1–2 : Full access, no prompts, no banners, no friction
     End of 2 : Inline note below result: 0 free runs left on THIS tool
     Run 3+   : HARD modal blocks submit — tool stays locked until signin

   localStorage keys (per-tool):
     ot_runs_{toolId}        (integer)  — runs used for this specific tool
     opora_anon_first_run_at (timestamp)— unix ms of very first run (any tool)
     opora_run_ids           (JSON arr) — last 10 run IDs (from worker)

   Worker receives: X-Opora-Tool-Id and X-Opora-Tool-Runs headers
   ============================================================ */

(function(global) {
  'use strict';

  // Same worker base as former opora-tools.js hub IIFE (was shared only by accident
  // when both lived in one file; gate must define its own).
  var API_BASE = (global.OPORA_WORKER_URL) || (global.OporaEdge && global.OporaEdge.WORKER_URL) || '';

  // Tools that DO count toward the per-tool anon limit
  var COUNTED_SLUGS = [
    'bid-drafter',
    'rfp-decoder',
    'margin-sentinel',
    'compliance-agent',
    'hire-right',
    'handbook-drafter',
    'customer-recovery',
    'lost-bid-autopsy',
    'review-response',
    'walkthrough-voice',
    'scope-of-work-generator',
  ];

  // Per-tool run limit for anonymous users
  var ANON_LIMIT = 2;

  // Tool ID → artifact tool string mapping (per SAVE_CTA_CONTRACT)
  var TOOL_ID_MAP = {
    'bid-drafter':     'bid_drafter',
    'rfp-decoder':     'rfp_decoder',
    'handbook-drafter':'handbook_drafter',
    'lost-bid-autopsy':'lost_bid_autopsy',
    'hire-right':      'hire_right',
    'compliance-agent':'compliance_check',
    'customer-recovery':'customer_recovery',
    'margin-sentinel': 'margin_sentinel',
    'review-response': 'review_response',
    'walkthrough-voice':'voice_walkthrough',
    'scope-of-work-generator': 'scope_of_work',
    'ask-opora':       'ask_opora',
    'ask-opora-deep':  'ask_opora',
    'ask-deep':        'ask_opora',
  };

  // Legacy global key (kept for backward compat — not incremented anymore)
  var KEY_FIRST_RUN_AT  = 'opora_anon_first_run_at';
  var KEY_RUN_IDS       = 'opora_run_ids';

  // ── Per-tool counter helpers ───────────────────────────────────────

  /**
   * Derive localStorage key for a given tool slug.
   * Format: ot_runs_{toolId}  e.g. ot_runs_bid-drafter
   */
  function toolRunKey(slug) {
    return 'ot_runs_' + slug;
  }

  function getToolRuns(slug) {
    try { return Math.max(0, parseInt(localStorage.getItem(toolRunKey(slug)) || '0', 10) || 0); }
    catch(e) { return 0; }
  }

  function setToolRuns(slug, n) {
    try { localStorage.setItem(toolRunKey(slug), String(n)); } catch(e) {}
  }

  function incrementToolRuns(slug) {
    // Record first-ever run timestamp
    try {
      if (!localStorage.getItem(KEY_FIRST_RUN_AT)) {
        localStorage.setItem(KEY_FIRST_RUN_AT, String(Date.now()));
      }
    } catch(e) {}
    var n = getToolRuns(slug) + 1;
    setToolRuns(slug, n);
    return n;
  }

  // ── Shared helpers ─────────────────────────────────────────────────

  function isSignedIn() {
    if (global.OporaEdge && typeof global.OporaEdge.isAuthed === 'function') {
      return global.OporaEdge.isAuthed();
    }
    if (global.OporaAccount && typeof global.OporaAccount.isSignedIn === 'function') {
      return global.OporaAccount.isSignedIn();
    }
    try {
      return !!(global.Clerk && global.Clerk.session);
    } catch (e) {
      return false;
    }
  }

  function isCounted(slug) {
    return COUNTED_SLUGS.indexOf(slug) !== -1;
  }

  // Store run IDs returned by worker (last 10)
  function recordRunId(id) {
    try {
      var arr = JSON.parse(localStorage.getItem(KEY_RUN_IDS) || '[]');
      if (!Array.isArray(arr)) arr = [];
      arr.push(id);
      if (arr.length > 10) arr = arr.slice(-10);
      localStorage.setItem(KEY_RUN_IDS, JSON.stringify(arr));
    } catch(e) {}
  }

  // ── Trial / signin URL helpers ─────────────────────────────────────

  var DASHBOARD_BASE = (global.OporaEdge && global.OporaEdge.DASHBOARD_URL) || 'https://dashboard.oporasupply.com';

  function dashboardUrl(path, returnTo) {
    try {
      var u = new URL(path, DASHBOARD_BASE);
      /* Do not add redirect_url — breaks dashboard for some browsers */
      return u.toString();
    } catch (e) {
      return DASHBOARD_BASE + path;
    }
  }

  function trialUrl(returnTo) {
    return dashboardUrl('/?intent=sign-up', returnTo);
  }

  function signinUrl(returnTo) {
    return dashboardUrl('/', returnTo);
  }

  /** Save / library / trial gate — in-place Clerk popup (tools Save behavior). */
  function openAuthForSave(intent) {
    intent = intent || 'sign-in';
    if (global.OporaEdge && typeof global.OporaEdge.stashAuthReturn === 'function') {
      global.OporaEdge.stashAuthReturn();
    } else {
      try { sessionStorage.setItem('_opora_auth_return', global.location.href); } catch (e) {}
    }
    if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function' && !global.OporaAuth.__oporaStub) {
      var isSaveish = intent === 'sign-in' || intent === 'signin' || intent === 'sign-up' || intent === 'signup';
      global.OporaAuth.openModal({
        intent: (intent === 'sign-up' || intent === 'signup') ? 'sign-up' : 'sign-in',
        inPlace: true,
        title: isSaveish ? 'Save this result' : undefined,
        tagline: isSaveish
          ? 'Create a free account to edit, duplicate, and access this result across devices.'
          : undefined
      });
      return;
    }
    var dash = DASHBOARD_BASE.replace(/\/$/, '');
    global.location.href = (intent === 'sign-up' || intent === 'signup')
      ? dash + '/?intent=sign-up'
      : dash + '/';
  }

  // ── Gate modal (run 4+ on a given tool) ───────────────────────────
  // Hard-blocks the submit for this tool until sign-in.

  function showHardModal(returnTo) {
    if (document.getElementById('ot-gate-modal')) return;

    var overlay = document.createElement('div');
    overlay.id = 'ot-gate-modal';
    overlay.className = 'ot-gate-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'ot-gate-modal-title');

    overlay.innerHTML =
      '<div class="ot-gate-modal">' +

      // Amber limit chip
      '<div class="ot-gate-modal-limit-chip" aria-hidden="true">' + ANON_LIMIT + ' of ' + ANON_LIMIT + ' free runs used</div>' +

      // Icon
      '<div class="ot-gate-modal-icon" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
      '<path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/>' +
      '</svg>' +
      '</div>' +

      // Headline
      '<h2 class="ot-gate-modal-title" id="ot-gate-modal-title">You&rsquo;ve explored Opora. Ready for more?</h2>' +

      // Value prop bullets
      '<ul class="ot-gate-modal-bullets">' +
      '<li><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg><span>50 tool runs per month for 30 days</span></li>' +
      '<li><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg><span>Save up to 100 outputs to your library</span></li>' +
      '<li><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg><span>Full access to all 14 AI tools</span></li>' +
      '</ul>' +

      // Primary CTA
      '<button type="button" class="ot-gate-modal-primary">Start 30-day free trial</button>' +

      // Secondary CTA
      '<a href="/pages/ai" class="ot-gate-modal-secondary">Browse free resources</a>' +

      // Microcopy
      '<p class="ot-gate-modal-microcopy">No credit card required. Cancel anytime. We don&rsquo;t sell your data.</p>' +

      '</div>';

    // Close on ESC only — clicking overlay doesn't dismiss (hard gate)
    var onKey = function(e) {
      if (e.key === 'Escape') {
        overlay.remove();
        document.removeEventListener('keydown', onKey);
      }
    };
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);

    var signupBtn = overlay.querySelector('.ot-gate-modal-primary');
    if (signupBtn) {
      signupBtn.addEventListener('click', function () {
        overlay.remove();
        document.removeEventListener('keydown', onKey);
        openAuthForSave('sign-up');
      });
    }

    // Trap focus on primary CTA
    setTimeout(function() {
      var cta = overlay.querySelector('.ot-gate-modal-primary');
      if (cta) cta.focus();
    }, 60);
  }

  // ── End-of-run-3 inline note ───────────────────────────────────────

  function buildRun3Note(returnTo) {
    var el = document.createElement('div');
    el.className = 'ot-gate-run3-note';
    el.setAttribute('role', 'note');
    el.innerHTML =
      '<div class="ot-gate-run3-inner">' +
      '<div class="ot-gate-run3-chip" aria-label="Run limit">0 free runs left on this tool</div>' +
      '<p class="ot-gate-run3-text">Save your work and unlock full access with a free 30-day trial &mdash; no card required.</p>' +
      '<button type="button" class="ot-gate-run3-cta">Start free trial</button>' +
      '</div>';
    return el;
  }

  // ── Sign-in save note ──────────────────────────────────────────────

  function buildSaveNote(returnTo) {
    var el = document.createElement('p');
    el.className = 'ot-gate-save-note';
    el.innerHTML =
      'Save this output &mdash; ' +
      '<button type="button" class="ot-gate-save-link">sign in</button> (free 30-day trial, no card).';
    var btn = el.querySelector('.ot-gate-save-link');
    if (btn) {
      btn.addEventListener('click', function () {
        if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function') {
          global.OporaAuth.openModal({
            intent: 'sign-up',
            inPlace: true,
            title: 'Save this result',
            tagline: 'Create a free account to edit, duplicate, and access this result across devices.'
          });
        } else {
          global.location.href = signinUrl(returnTo);
        }
      });
    }
    return el;
  }

  // ── Block submit if this tool is at limit ─────────────────────────

  function guardSubmit(form) {
    if (isSignedIn()) return true;

    var slug = (form.dataset.slug || '');
    if (!isCounted(slug)) return true;

    var runs = getToolRuns(slug);
    if (runs >= ANON_LIMIT) {
      showHardModal(global.location.pathname);
      return false;
    }
    return true;
  }

  // ── Post-render gate UI ────────────────────────────────────────────

  function findExportRow(parent) {
    if (!parent) return null;
    var row = parent.querySelector('.d4-export-row');
    if (row) return row;
    var kids = parent.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].querySelector && kids[i].querySelector('.opora-export-btn')) {
        kids[i].classList.add('d4-export-row');
        return kids[i];
      }
    }
    return null;
  }

  function applyPostRenderGate(outputEl, slug) {
    if (isSignedIn()) return;
    if (!isCounted(slug)) return;

    var runs = getToolRuns(slug);

    if (runs === ANON_LIMIT) {
      var parent = outputEl.parentNode;
      var existing = parent && parent.querySelector('.ot-gate-run3-note');
      if (!existing && parent) {
        var note = buildRun3Note(global.location.pathname);
        var exportRow = findExportRow(parent);
        parent.insertBefore(note, exportRow || outputEl.nextSibling);
        var trialBtn = note.querySelector('.ot-gate-run3-cta');
        if (trialBtn) {
          trialBtn.addEventListener('click', function () {
            openAuthForSave('sign-up');
          });
        }
      }
    }
  }

  // ── Save bar ───────────────────────────────────────────────────────
  // Injected below the output element after a successful result.
  // Per SAVE_CTA_CONTRACT §1, §3, §4, §5, §6.

  function getAuthToken() {
    return global.__clerk_token || localStorage.getItem('opora_auth_token') || null;
  }

  /**
   * Show a brief toast notification.
   * @param {string} html  - innerHTML for the toast (may include links)
   * @param {'success'|'error'} type
   */
  function showToast(html, type) {
    var existing = document.getElementById('ot-save-toast');
    if (existing) existing.remove();
    var t = document.createElement('div');
    t.id = 'ot-save-toast';
    t.className = 'ot-save-toast ot-save-toast--' + (type || 'success');
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    t.innerHTML = html;
    document.body.appendChild(t);
    // Auto-dismiss after 5s
    setTimeout(function() { if (t.parentNode) t.remove(); }, 5000);
  }

  /**
   * Open the save dialog to name the artifact, then call /api/artifacts.
   * @param {string} slug         - tool slug (e.g. 'bid-drafter')
   * @param {string} outputText   - LLM output markdown (body_markdown)
   * @param {object} safeMetadata - whitelisted metadata fields
   * @param {string} suggestedTitle
   */
  function openSaveDialog(slug, outputText, safeMetadata, suggestedTitle) {
    var toolId = TOOL_ID_MAP[slug] || slug.replace(/-/g, '_');
    if (document.getElementById('ot-save-dialog')) return;

    var overlay = document.createElement('div');
    overlay.id = 'ot-save-dialog';
    overlay.className = 'ot-save-dialog-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'ot-save-dialog-title');

    overlay.innerHTML =
      '<div class="ot-save-dialog">' +
      '<h2 class="ot-save-dialog-title" id="ot-save-dialog-title">Save to library</h2>' +
      '<label class="ot-save-dialog-label" for="ot-save-title-input">Name this artifact</label>' +
      '<input class="ot-save-dialog-input" id="ot-save-title-input" type="text" maxlength="200"' +
      ' value="' + escapeDialogAttr(suggestedTitle) + '" />' +
      '<div class="ot-save-dialog-actions">' +
      '<button class="ot-save-dialog-confirm" type="button">Save</button>' +
      '<button class="ot-save-dialog-cancel" type="button">Cancel</button>' +
      '</div>' +
      '</div>';

    var onKey = function(e) {
      if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', onKey); }
    };
    document.addEventListener('keydown', onKey);

    overlay.querySelector('.ot-save-dialog-cancel').addEventListener('click', function() {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    });

    overlay.querySelector('.ot-save-dialog-confirm').addEventListener('click', function() {
      var titleInput = overlay.querySelector('#ot-save-title-input');
      var title = (titleInput.value || '').trim();
      if (!title) { titleInput.focus(); return; }
      overlay.remove();
      document.removeEventListener('keydown', onKey);
      doSave(slug, title, outputText, safeMetadata);
    });

    document.body.appendChild(overlay);
    setTimeout(function() {
      var inp = overlay.querySelector('#ot-save-title-input');
      if (inp) { inp.focus(); inp.select(); }
    }, 60);
  }

  function escapeDialogAttr(s) {
    return String(s || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;').slice(0, 200);
  }

  /**
   * POST to /api/artifacts with Clerk token.
   */
  function doSave(slug, title, outputText, safeMetadata) {
    var toolSlug = TOOL_ID_MAP[slug] || String(slug || 'unknown').replace(/-/g, '_');
    var workerBase = (API_BASE || (window.OporaEdge && window.OporaEdge.WORKER_URL) || window.OPORA_WORKER_URL || 'https://api.oporasupply.com').replace(/\/$/, '');
    var dashUrl = (window.OporaEdge && window.OporaEdge.DASHBOARD_CUSTOM_URL) || 'https://dashboard.oporasupply.com';

    function runSave(headers) {
      var body = {
        tool: toolSlug,
        tool_slug: toolSlug,
        title: title,
        body_markdown: outputText,
        content: outputText,
        metadata: safeMetadata || {}
      };
      if (window.OporaAccountFile && typeof window.OporaAccountFile.withAccountBody === 'function') {
        body = window.OporaAccountFile.withAccountBody(body);
      }
      showToast('Saving&hellip;', 'success');
      fetch(workerBase + '/api/artifacts', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, headers || {}),
        body: JSON.stringify(body)
      })
      .then(function(res) {
        return res.json().then(function(data) {
          if (!res.ok || data.ok === false) {
            if (data && data.code === 'PII_DETECTED') {
              showToast(
                'We can\'t save this output — it contains sensitive data. ' +
                '<a href="/pages/terms#sensitive-data">Learn more</a>',
                'error'
              );
            } else if (res.status === 401 || (data && data.code === 'UNAUTHORIZED')) {
              try {
                sessionStorage.setItem('opora_pending_save', JSON.stringify({
                  toolId: toolSlug, title: title, outputText: outputText, safeMetadata: safeMetadata
                }));
              } catch(e) {}
              openAuthForSave('sign-in');
            } else if (res.status >= 500) {
              showToast('Save failed, try again.', 'error');
            } else if (data && data.code === 'BODY_TOO_LARGE') {
              showToast('This output is too large to save (max 500KB). Try saving a shorter section.', 'error');
            } else {
              var errMsg = (data.error && data.error.message) || 'Could not save. Try again.';
              showToast(errMsg, 'error');
            }
            return;
          }
          showToast(
            '\u2713 Saved &mdash; <a href="' + dashUrl + '/?view=workspace&ws=files" class="ot-toast-link">View in My Files</a>',
            'success'
          );
          try {
            document.dispatchEvent(new CustomEvent('opora:artifact-saved', {
              detail: { toolId: toolSlug, slug: toolSlug }
            }));
          } catch(e) {}
        });
      })
      .catch(function() {
        showToast('Could not connect to save. Check your internet connection.', 'error');
      });
    }

    if (window.OporaAccount && typeof window.OporaAccount.getAuthHeaders === 'function') {
      window.OporaAccount.getAuthHeaders().then(function(headers) {
        if (!headers || !headers.Authorization) {
          try {
            sessionStorage.setItem('opora_pending_save', JSON.stringify({
              toolId: toolSlug, title: title, outputText: outputText, safeMetadata: safeMetadata
            }));
          } catch(e) {}
          openAuthForSave('sign-in');
          return;
        }
        runSave(headers);
      });
      return;
    }

    var token = getAuthToken();
    if (!token) {
      try {
        sessionStorage.setItem('opora_pending_save', JSON.stringify({
          toolId: toolSlug, title: title, outputText: outputText, safeMetadata: safeMetadata
        }));
      } catch(e) {}
      openAuthForSave('sign-in');
      return;
    }
    runSave({ 'Authorization': 'Bearer ' + token });
  }

  /**
   * Build the save bar DOM element for insertion after a result.
   * @param {string} slug
   * @param {function} getOutputText  - called lazily to capture current output HTML/text
   * @param {function} getSafeMetadata - called lazily to capture safe metadata
   * @param {string}   suggestedTitle
   * @returns {HTMLElement}
   */
  function buildSaveBar(slug, getOutputText, getSafeMetadata, suggestedTitle) {
    var bar = document.createElement('div');
    bar.className = 'ot-save-bar';
    bar.setAttribute('aria-live', 'polite');

    var btn = document.createElement('button');
    btn.className = 'ot-save-btn';
    btn.type = 'button';
    btn.textContent = 'Save to library';

    btn.addEventListener('click', function() {
      if (!isSignedIn() && !getAuthToken()) {
        // Stash pending save details so auth can resume it
        try {
          var toolId = TOOL_ID_MAP[slug] || slug.replace(/-/g, '_');
          sessionStorage.setItem('opora_pending_save', JSON.stringify({
            toolId: toolId,
            title: suggestedTitle || '',
            outputText: getOutputText(),
            safeMetadata: getSafeMetadata()
          }));
        } catch(e) {}
        openAuthForSave('sign-in');
        return;
      }
      openSaveDialog(slug, getOutputText(), getSafeMetadata(), suggestedTitle || '');
    });

    bar.appendChild(btn);
    return bar;
  }

  // Check for pending save on page load (after auth completes)
  function checkPendingSave() {
    try {
      var raw = sessionStorage.getItem('opora_pending_save');
      if (!raw) return;
      var pending = JSON.parse(raw);
      if (!pending || !pending.toolId) return;

      function finish(token) {
        if (!token) return;
        sessionStorage.removeItem('opora_pending_save');
        showToast('Signed in \u2713 Saving your work\u2026', 'success');
        doSave(pending.toolId, pending.title, pending.outputText, pending.safeMetadata);
      }

      var token = getAuthToken();
      if (token) { finish(token); return; }
      if (global.Clerk && global.Clerk.session && typeof global.Clerk.session.getToken === 'function') {
        global.Clerk.session.getToken().then(function (t) {
          if (t) global.__clerk_token = t;
          finish(t);
        });
      }
    } catch(e) {}
  }

  // ── Patch wire() ──────────────────────────────────────────────────

  function patchWireWithGate() {
    var tools = global.OporaTools;
    if (!tools || !tools.wire) return;

    var _origWire = tools.wire;
    tools.wire = function(slug) {
      _origWire.call(tools, slug);

      var formId   = 'opora-' + slug + '-form';
      var outputId = 'opora-' + slug + '-output';
      var form     = document.getElementById(formId);
      var outputEl = document.getElementById(outputId);
      if (!form || !outputEl) return;

      // Tag the form with its slug for guardSubmit
      form.dataset.slug = slug;

      // Pre-submit guard (capture phase — fires first)
      var pendingCompletedRun = false;

      form.addEventListener('submit', function(e) {
        if (!guardSubmit(form)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        try { delete outputEl.dataset.oporaToolRunTracked; } catch (err) {}
        // Count only after a completed result renders (see MutationObserver below)
        if (!isSignedIn() && isCounted(slug)) {
          pendingCompletedRun = true;
        }
      }, true);

      // MutationObserver: watch for real result, then count + inject save bar
      var observer = new MutationObserver(function() {
        if (!outputEl.querySelector('.ot-output-inner')) return;
        if (pendingCompletedRun && !isSignedIn() && isCounted(slug)) {
          incrementToolRuns(slug);
          pendingCompletedRun = false;
        }
        applyPostRenderGate(outputEl, slug);
        injectSaveBar(outputEl, slug);
        try {
          if (!outputEl.dataset.oporaToolRunTracked) {
            outputEl.dataset.oporaToolRunTracked = '1';
            if (typeof global.oporaTrackToolRun === 'function') {
              global.oporaTrackToolRun(slug);
            }
          }
        } catch (e) {}
      });
      observer.observe(outputEl, { childList: true, subtree: false });

      // Build-10: wire autosave for this tool
      wireAutosave(slug, form, outputEl);
    };
  }

  /**
   * Inject the Save bar below the output element (if not already present).
   * Extracts output text and metadata from the rendered DOM.
   */
  function injectSaveBar(outputEl, slug) {
    // Only for counted tools
    if (!isCounted(slug)) return;
    // Avoid duplicate bars
    if (outputEl.parentNode && outputEl.parentNode.querySelector('.ot-save-bar')) return;

    // Auto-suggested title per SAVE_CTA_CONTRACT §2
    var toolName = slug.replace(/-/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
    var now = new Date();
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    var datePart = months[now.getMonth()] + ' ' + now.getDate();
    var suggestedTitle = toolName + ' \u2014 ' + datePart;

    // Capture output text lazily (called when user clicks Save)
    function getOutputText() {
      var inner = outputEl.querySelector('.ot-output-inner');
      return inner ? (inner.innerText || inner.textContent || '') : '';
    }

    // Collect safe metadata from output element data attributes (set by render layer)
    function getSafeMetadata() {
      var meta = {};
      var el = outputEl.querySelector('[data-safe-meta]');
      if (el) {
        try { meta = JSON.parse(el.getAttribute('data-safe-meta') || '{}'); } catch(e) {}
      }
      return meta;
    }

    var bar = buildSaveBar(slug, getOutputText, getSafeMetadata, suggestedTitle);
    if (outputEl.parentNode) {
      outputEl.parentNode.insertBefore(bar, outputEl.nextSibling);
    }
  }

  // ── Patch fetch to inject X-Opora-Tool-Id and X-Opora-Tool-Runs headers ──

  (function patchFetch() {
    var _origFetch = global.fetch;
    global.fetch = function(url, opts) {
      if (typeof url === 'string' &&
          (url.includes('api.oporasupply.com') || url.includes('opora-ai.mshabliy.workers.dev') || url.startsWith('/api/'))) {
        // Determine tool slug from current page URL
        var pathSlug = global.location.pathname.split('/').filter(Boolean).pop() || '';
        var slug = pathSlug.replace(/^pages-?/, '');
        opts = Object.assign({}, opts || {});
        var toolRuns = isCounted(slug) ? getToolRuns(slug) : 0;
        if (opts.headers instanceof Headers) {
          opts.headers = new Headers(opts.headers);
          opts.headers.set('X-Opora-Tool-Id', slug);
          opts.headers.set('X-Opora-Tool-Runs', String(toolRuns));
        } else {
          opts.headers = Object.assign({}, opts.headers || {});
          opts.headers['X-Opora-Tool-Id'] = slug;
          opts.headers['X-Opora-Tool-Runs'] = String(toolRuns);
        }
      }
      return _origFetch.apply(this, arguments);
    };
  })();

  // ── Autosave wiring (Build-10) ────────────────────────────────────────────
  //
  // Called from patchWireWithGate after each tool is wired. Initializes
  // OporaAutosave for the given slug with a form-based getInputs/setInputs pair.
  // The autosave module must be loaded before opora-tools.js for this to work.
  // If OporaAutosave is not loaded, this is a no-op.

  function wireAutosave(slug, form, outputEl) {
    if (typeof global.OporaAutosave === 'undefined' || typeof global.OporaAutosave.init !== 'function') {
      return; // Module not loaded — skip
    }

    // Banner mounts above the form, not above the output
    var formWrapper = form.parentNode || null;

    var instance = global.OporaAutosave.init({
      toolId: slug,
      getInputs: function() {
        return global.OporaAutosave.serializeForm(form);
      },
      setInputs: function(inputs) {
        global.OporaAutosave.hydrateForm(form, inputs);
      },
      restoreBannerContainer: formWrapper,
    });

    // Wire save button success callback to clear the draft.
    // We do this by patching doSave via MutationObserver on the toast container,
    // and also by intercepting the injectSaveBar save confirmation.
    // The cleanest path: patch the fetch call to /api/artifacts for this form's
    // tool. When it returns 201, call instance.clear().
    // Since fetch is already patched by patchFetch above, we add a custom
    // window listener that opora-tools fires after successful save.
    if (instance) {
      // Listen for custom 'opora:artifact-saved' event fired in doSave on success
      document.addEventListener('opora:artifact-saved', function(e) {
        // Only clear if this event is for the current tool
        var savedTool = e.detail && (e.detail.slug || e.detail.toolId);
        var myToolId = TOOL_ID_MAP[slug] || slug.replace(/-/g, '_');
        if (!savedTool || savedTool === slug || savedTool === myToolId) {
          instance.clear();
        }
      });
    }
  }

  // ── Build-11: Smart Defaults Hydration ──────────────────────────────────
  //
  // After page load (and after autosave restore), fetch GET /api/account/me
  // (if signed in) and apply default values to any empty input/select
  // that carries data-default-key="<key>".
  //
  // Default keys:
  //   state        → user.default_state
  //   labor_rate   → user.default_labor_rate
  //   margin_pct   → user.default_margin_pct
  //   vertical     → user.default_vertical
  //
  // Soft hydrate: only fills EMPTY inputs. Never overrides existing values.
  // Cache: /api/account/me result stored in sessionStorage for 60s.

  var DEFAULTS_CACHE_KEY = 'opora_defaults_cache';
  var DEFAULTS_CACHE_TTL = 60; // seconds

  function getCachedDefaults() {
    try {
      var raw = sessionStorage.getItem(DEFAULTS_CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.ts || !obj.user) return null;
      var age = Math.floor(Date.now() / 1000) - obj.ts;
      if (age > DEFAULTS_CACHE_TTL) {
        sessionStorage.removeItem(DEFAULTS_CACHE_KEY);
        return null;
      }
      return obj.user;
    } catch(e) { return null; }
  }

  function setCachedDefaults(user) {
    try {
      sessionStorage.setItem(DEFAULTS_CACHE_KEY, JSON.stringify({
        ts: Math.floor(Date.now() / 1000),
        user: user
      }));
    } catch(e) {}
  }

  function applyDefaultsToPage(user) {
    if (!user) return;
    var keyMap = {
      state:      user.default_state,
      labor_rate: user.default_labor_rate,
      margin_pct: user.default_margin_pct,
      vertical:   user.default_vertical,
    };
    Object.keys(keyMap).forEach(function(key) {
      var defVal = keyMap[key];
      if (defVal == null || defVal === '') return;
      var els = document.querySelectorAll('[data-default-key="' + key + '"]');
      els.forEach(function(el) {
        // Soft: only apply if empty
        var isEmpty = (el.value === '' || el.value === null || el.value === undefined);
        // For selects, also check if no non-empty option is selected
        if (el.tagName === 'SELECT' && el.value !== '') isEmpty = false;
        if (!isEmpty) return;
        el.value = String(defVal);
        // Dispatch change event so any listeners (e.g. reactive frameworks) react
        try { el.dispatchEvent(new Event('change', { bubbles: true })); } catch(e) {}
      });
    });
  }

  function hydrateDefaults() {
    // Gate on Clerk presence — don't fetch on anonymous pages
    var token = getAuthToken();
    if (!token) return;

    // Check cache first
    var cached = getCachedDefaults();
    if (cached) {
      applyDefaultsToPage(cached);
      return;
    }

    // Determine API base
    var apiBase = (typeof global.OPORA_WORKER_URL !== 'undefined' && global.OPORA_WORKER_URL)
      ? global.OPORA_WORKER_URL : '';

    fetch(apiBase + '/api/account/me', {
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    }).then(function(res) {
      if (!res.ok) return null;
      return res.json();
    }).then(function(data) {
      if (!data) return;
      var user = data.user || data;
      setCachedDefaults(user);
      applyDefaultsToPage(user);
    }).catch(function() { /* non-fatal */ });
  }


  // ── Artifact hydration (?artifact=ID on tool page load) ──────────────────────
  // Build-8: When a tool page is opened with ?artifact=ID, fetch the artifact
  // via GET /api/artifacts/:id and hydrate form + output.
  // Calls window.OPORA_TOOL_HYDRATE({ artifact, body_markdown }) if defined;
  // otherwise falls back to generic form-field + output-container hydration.

  function getClerkTokenForHydration() {
    try {
      return (window.__clerk_token) || localStorage.getItem('opora_auth_token') || null;
    } catch(e) { return null; }
  }

  async function hydrateFromArtifact(artifactId) {
    var token = getClerkTokenForHydration();
    if (!token) return; // not signed in — skip silently

    try {
      var resp = await fetch(API_BASE + '/api/artifacts/' + encodeURIComponent(artifactId), {
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
      });
      if (!resp.ok) return; // not found / not owned — skip silently

      var data = await resp.json();
      var bodyMarkdown = data.body_markdown || '';

      // Bespoke hook: tool page implements window.OPORA_TOOL_HYDRATE(payload)
      if (typeof window.OPORA_TOOL_HYDRATE === 'function') {
        try { window.OPORA_TOOL_HYDRATE({ artifact: data, body_markdown: bodyMarkdown }); }
        catch(e) { console.warn('[OporaTools] OPORA_TOOL_HYDRATE threw:', e); }
        return;
      }

      // Generic fallback hydration
      // 1. Set form fields from artifact.metadata keys
      if (data.metadata && typeof data.metadata === 'object') {
        Object.keys(data.metadata).forEach(function(key) {
          var val = data.metadata[key];
          var el = document.getElementById(key) || document.querySelector('[name="' + key + '"]');
          if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) {
            try { el.value = val; } catch(e) {}
          }
        });
      }

      // 2. Render body_markdown into output container
      if (bodyMarkdown) {
        var outputEl =
          document.querySelector('[data-output-target]') ||
          document.querySelector('[id$="-output"]') ||
          document.querySelector('.ot-output') ||
          document.querySelector('[class*="output"]');
        if (outputEl) {
          var pre = document.createElement('pre');
          pre.style.cssText = 'white-space:pre-wrap;word-break:break-word;font-family:inherit;margin:0;padding:16px;';
          pre.textContent = bodyMarkdown; // textContent for safety (no innerHTML injection)
          outputEl.innerHTML = '';
          outputEl.appendChild(pre);
          outputEl.style.display = '';
          try { outputEl.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch(e) {}
        }
      }
    } catch(e) {
      console.warn('[OporaTools] Artifact hydration error:', e);
    }
  }

  function checkArtifactParam() {
    try {
      var params = new URLSearchParams(window.location.search);
      var artifactId = params.get('artifact');
      if (!artifactId) return;
      // Delay to allow Clerk to set window.__clerk_token if it hasn't yet
      var token = getClerkTokenForHydration();
      var delay = token ? 0 : 1800;
      setTimeout(function() { hydrateFromArtifact(artifactId); }, delay);
    } catch(e) { console.warn('[OporaTools] checkArtifactParam error:', e); }
  }

  // ── Init ───────────────────────────────────────────────────────────

  function init() {
    patchWireWithGate();
    checkPendingSave();
    global.addEventListener('opora:signed-in', function () {
      setTimeout(checkPendingSave, 500);
    });
    // Apply smart defaults after restore check so user-restored values take priority
    hydrateDefaults();
    // Build-8: check for ?artifact=ID param for tool page re-hydration
    checkArtifactParam();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  global.OporaGate = {
    getToolRuns:  getToolRuns,
    setToolRuns:  setToolRuns,
    isSignedIn:   isSignedIn,
    isCounted:    isCounted,
    guardSubmit:  guardSubmit,
    showHardModal: showHardModal,
    openAuthForSave: openAuthForSave,
    applyPostRenderGate: applyPostRenderGate,
    openSaveDialog: openSaveDialog,
    hydrateDefaults: hydrateDefaults,   // Build-11: callable from tool pages
    reset: function(slug) {
      // If slug given, reset only that tool; otherwise reset all
      try {
        if (slug) {
          localStorage.removeItem(toolRunKey(slug));
        } else {
          COUNTED_SLUGS.forEach(function(s) { localStorage.removeItem(toolRunKey(s)); });
          localStorage.removeItem(KEY_FIRST_RUN_AT);
          localStorage.removeItem(KEY_RUN_IDS);
        }
      } catch(e) {}
    },
    ANON_LIMIT:    ANON_LIMIT,
    COUNTED_SLUGS: COUNTED_SLUGS,
    TOOL_ID_MAP:   TOOL_ID_MAP,
    hydrateFromArtifact: hydrateFromArtifact,  // Build-8: programmatic hydration
  };

})(window);
